/* AECO 3D hero.
   One locked shot. Two beats: swipe right opens the right temple, swipe left the
   left one, in either order. Once both are out the lens walks the tint ring.
   Nothing here touches the page scroll - there is no wheel handler and the canvas
   is touch-action:pan-y, so a vertical drag scrolls the document as usual. */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

// A quarter turn, measured, not eyeballed: sweeping the hinge and watching the
// temple's depth, its bounding box is thinnest at exactly 90 - that is the angle
// where the arm lies flat along the back of the front. Flip a sign if a temple
// folds outward.
const FOLD_L = -Math.PI / 2;
const FOLD_R =  Math.PI / 2;
const FOLD_DUR = 1.55;       // seconds per temple swing
// The front's depth is baked into the .glb by _thin-front.py - the customiser
// renders the same file through <model-viewer>, which has no geometry hook, so a
// runtime squash here would have left the two views disagreeing.

// The camera never moves. It sits BEHIND the folded frame, looking at the back of
// it - the shot the temples were designed to open into, and the one the wordmark
// reads through. No orbit, no dolly, no scroll hook.
const CAM_POS  = [ 0.000, 0.030,-0.340];
const CAM_LOOK = [ 0.000, 0.000, 0.020];
const MODEL_C  = new THREE.Vector3(0, 0.025, -0.05);

// Temple_R sits at +X. A camera IN FRONT of the frame sees +X on the right of the
// screen; a camera behind it sees the mirror image, so the two sides swap. Derive
// it rather than hard-coding, or moving the camera silently reverses the swipes.
const FRONTAL = CAM_POS[2] > 0;
const SIDE = FRONTAL ? { right: 1, left: 0 } : { right: 0, left: 1 };

const BASE_A = 16 / 9, FOV = 30, FOV_CAP = 46;
// Drops the whole shot - frame and wordmark together - toward the middle of the
// section. A view offset, not a camera move: the pose and the perspective are
// untouched, only the window we render through slides up. Fraction of the height.
const SHIFT_Y = 0.09;
// Phone portrait: the shot sits too low and the wordmark reads small. Lift the
// whole shot and let the wordmark take more of the narrow width.
const PORTRAIT_A = 0.75;
const SHIFT_Y_PORTRAIT = 0.02;
const WM_BOOST_PORTRAIT = 1.45;
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

function boot(root) {
  if (root.dataset.h3dOn) return;
  root.dataset.h3dOn = '1';

  const stage   = root.querySelector('[data-h3d-stage]');
  const loadEl  = root.querySelector('[data-h3d-load]');
  const barEl   = root.querySelector('[data-h3d-bar]');
  const errEl   = root.querySelector('[data-h3d-error]');
  const tintEl  = root.querySelector('[data-h3d-tint]');
  const capT    = root.querySelector('[data-h3d-capt]');
  const capB    = root.querySelector('[data-h3d-capb]');
  const hintSvg = root.querySelector('[data-h3d-spinhint] svg');

  const cfg = JSON.parse(root.querySelector('[data-h3d-config]').textContent);
  const VARIANTS = cfg.tints;
  const N = VARIANTS.length;
  const TINT_HOLD = cfg.tint_hold;
  const BRAND_OPACITY = cfg.wordmark_opacity;
  const COPY = cfg.copy;

  /* ---------- renderer ---------- */
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  const COARSE = matchMedia('(pointer:coarse)').matches;
  renderer.setPixelRatio(Math.min(devicePixelRatio, COARSE ? 1.25 : 1.5));
  // a material with transmission > 0 makes three.js re-render the whole scene into
  // a transmission target every frame. at full res that alone halves the framerate.
  renderer.transmissionResolutionScale = 0.4;
  renderer.setClearAlpha(0);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.02;
  stage.append(renderer.domElement);

  const scene = new THREE.Scene();

  /* ---------- studio environment ----------
     The acetate carries almost no diffuse, so everything you see on it is this
     scene reflected. The two narrow hot strips are what draw the long specular
     streak down the brow bar - a full-width panel there turns the frame to chrome. */
  function studioEnv() {
    const s = new THREE.Scene();
    s.background = new THREE.Color(0x070a16);
    const box = new THREE.BoxGeometry();
    const light = (w, h, d, x, y, z, color, power) => {
      const m = new THREE.Mesh(box, new THREE.MeshBasicMaterial({ color }));
      m.material.color.multiplyScalar(power);
      m.scale.set(w, h, d); m.position.set(x, y, z);
      s.add(m);
    };
    light(0.5,  6,    1.7, -3.2,  0.4,  0.6, 0xffffff, 6.4);   // tall key softbox, camera left
    light(0.35, 5,    1.4,  3.0, -0.2, -0.4, 0xd6dde6, 4.2);   // fill, camera right
    light(5,    0.5,  1.9,  0,    3.0,  0.3, 0xffffff, 3.4);   // overhead strip
    light(5,    3,    0.4,  0,   -0.6, -3.2, 0x2e3038, 2.2);   // back wall, neutral - a violet one dyes black acetate blue
    light(3.4,  0.10, 0.10, 0,   -1.6,  1.9, 0x9ec4ff, 6.0);   // low kicker, lifts the bottom rim off the black
    light(0.55, 3.0,  0.3, -1.45, 0.5,  4.2, 0xf2f6ff, 1.0);
    light(0.55, 3.0,  0.3,  1.45, 0.5,  4.2, 0xdfeaff, 0.8);
    light(4.4,  3.0,  0.3,  0,    0.5, -4.2, 0xbcd2ff, 1.6);
    light(0.3,  3.0,  4.4, -4.2,  0.5,  0,   0xffffff, 2.2);   // side walls, so a temple never goes flat
    light(0.3,  3.0,  4.4,  4.2,  0.5,  0,   0xc8dcff, 1.8);
    return s;
  }
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(studioEnv(), 0.03).texture;

  /* ---------- camera ---------- */
  const camera = new THREE.PerspectiveCamera(FOV, 1, 0.004, 40);
  const halfH = Math.tan(THREE.MathUtils.degToRad(FOV) / 2) * BASE_A;
  let fitScale = 1;
  let wmBoost = 1;

  // A phone is ~0.46 aspect against a 16:9 desk. Left alone that crops the frame in
  // half. Keep the HORIZONTAL field constant instead: widen the fov to a cap, then
  // take whatever the cap could not recover out of the dolly.
  function fitView(w, h) {
    const a = w / h;
    const fov = a >= BASE_A ? FOV
      : Math.min(FOV_CAP, THREE.MathUtils.radToDeg(2 * Math.atan(halfH / a)));
    camera.fov = fov;
    camera.aspect = a;
    const portrait = a < PORTRAIT_A;
    wmBoost = portrait ? WM_BOOST_PORTRAIT : 1;
    const shiftY = portrait ? SHIFT_Y_PORTRAIT : SHIFT_Y;
    camera.setViewOffset(w, h, 0, -shiftY * h, w, h);   // updates the projection
    fitScale = Math.max(1, 0.62 * halfH / (Math.tan(THREE.MathUtils.degToRad(fov) / 2) * a));
  }

  // No OrbitControls. They bind wheel and pointer on the canvas, which is exactly
  // what would steal the page scroll; and a hero that can be spun off-axis stops
  // being a shot. The camera is one pose, re-derived only on resize.
  const camTarget = new THREE.Vector3();
  const camPos    = new THREE.Vector3();
  const camDir    = new THREE.Vector3();
  function placeCamera() {
    camTarget.fromArray(CAM_LOOK);
    camPos.fromArray(CAM_POS);
    if (fitScale > 1) {                    // portrait: pull back instead of cropping
      camTarget.lerp(MODEL_C, 0.75);
      camPos.sub(camTarget).multiplyScalar(fitScale).add(camTarget);
    }
    camera.position.copy(camPos);
    camera.lookAt(camTarget);
    // The wordmark is a plane in the scene, so at an off-axis camera a fixed world
    // position projects off to one side. Hang it on the camera's own axis instead
    // and it lands dead centre behind the frame at any viewport.
    camDir.copy(camTarget).sub(camPos).normalize();
    brand3d.position.copy(camPos).addScaledVector(camDir, 0.46);
    brand3d.position.y += cfg.wordmark_lift;
    brand3d.quaternion.copy(camera.quaternion);
  }

  /* ---------- lights ---------- */
  const key = new THREE.DirectionalLight(0xfff4e8, 1.45);
  key.position.set(0.45, 0.55, 0.65);
  const rim = new THREE.DirectionalLight(0xcfd8e4, 2.1);
  rim.position.set(-0.65, 0.25, -0.55);
  // tuned against black acetate; on the white frame a hotter one stains the temple
  const under = new THREE.DirectionalLight(0x9aa0ad, 0.26);
  under.position.set(0, -0.6, 0.3);
  const fill = new THREE.DirectionalLight(0xdfe9ff, 0.20);
  scene.add(key, rim, under, fill, new THREE.AmbientLight(0xffffff, 0.16));

  /* ---------- wordmark, drawn in 3D ----------
     It cannot live in the DOM: the bloom and output passes blit opaque full-screen
     quads, so nothing behind the canvas is ever visible. */
  const brand3d = (() => {
    const c = document.createElement('canvas');
    c.width = 2048; c.height = 640;
    const g = c.getContext('2d');
    const draw = () => {
      g.clearRect(0, 0, 2048, 640);
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = '500 320px Poppins, Inter, system-ui, sans-serif';
      g.letterSpacing = '-6px';
      // a longer name ("Galaxsee 001") shrinks to fit instead of clipping the texture
      const ww = g.measureText(cfg.wordmark).width;
      if (ww > 1960) g.font = `500 ${Math.floor(320 * 1960 / ww)}px Poppins, Inter, system-ui, sans-serif`;
      const grd = g.createLinearGradient(0, 150, 0, 500);
      grd.addColorStop(0.00, '#ffffff');
      grd.addColorStop(0.30, '#e2e7ee');
      grd.addColorStop(0.62, '#9aa3ae');
      grd.addColorStop(1.00, '#4c545e');
      g.fillStyle = grd;
      g.fillText(cfg.wordmark, 1024, 330);
      // "— AN AECO ESSENTIALS —" rides in the same texture, so it tracks the
      // wordmark's position and scale at every width for free
      if (cfg.eyebrow) {
        const t = cfg.eyebrow.toUpperCase();
        g.font = '400 50px Poppins, Inter, system-ui, sans-serif';
        g.letterSpacing = '20px';
        g.fillStyle = '#e8edf3';
        g.fillText(t, 1034, 100);          // +10: letterSpacing trails the last glyph
        const half = g.measureText(t).width / 2 + 34;
        g.fillRect(1024 - half - 110, 99, 110, 3);
        g.fillRect(1024 + half, 99, 110, 3);
      }
    };
    draw();
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    document.fonts.ready.then(() => { draw(); tex.needsUpdate = true; });  // redraw once Poppins lands
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(0.60, 0.1875),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, depthWrite: false }));
    scene.add(m);                     // placeCamera() owns its position and facing
    return m;
  })();

  /* ---------- starfield ---------- */
  const stars = (() => {
    const C = COARSE ? 900 : 1800, p = new Float32Array(C * 3);
    for (let i = 0; i < C; i++) {
      const r = 1 + Math.random() * 5, t = Math.random() * Math.PI * 2;
      const u = Math.random() * 2 - 1, s = Math.sqrt(1 - u * u);
      p.set([r * s * Math.cos(t), r * u * 0.6, r * s * Math.sin(t)], i * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(p, 3));
    const pts = new THREE.Points(g, new THREE.PointsMaterial({
      size: 0.011, sizeAttenuation: true, color: 0xb9cbef,
      transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }));
    scene.add(pts);
    return pts;
  })();

  /* ---------- shooting stars ----------
     Billboarded streaks: a bright head dragging a thin decaying tail, aligned to
     the travel direction. One at a time, at a random interval, far behind. */
  const meteors = (() => {
    const c = document.createElement('canvas'); c.width = 256; c.height = 32;
    const g = c.getContext('2d');
    const grd = g.createLinearGradient(0, 0, 256, 0);
    grd.addColorStop(0.00, 'rgba(90,130,220,0)');
    grd.addColorStop(0.55, 'rgba(120,160,235,.07)');
    grd.addColorStop(0.84, 'rgba(180,208,255,.30)');
    grd.addColorStop(0.96, 'rgba(240,248,255,.92)');
    grd.addColorStop(0.99, 'rgba(255,255,255,1)');
    grd.addColorStop(1.00, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 256, 32);
    const vert = g.createLinearGradient(0, 0, 0, 32);   // taper it into a spindle, not a bar
    vert.addColorStop(0, 'rgba(0,0,0,0)');
    vert.addColorStop(0.5, 'rgba(0,0,0,1)');
    vert.addColorStop(1, 'rgba(0,0,0,0)');
    g.globalCompositeOperation = 'destination-in';
    g.fillStyle = vert; g.fillRect(0, 0, 256, 32);

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const pool = [];
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 0.026), new THREE.MeshBasicMaterial({
        map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
      m.visible = false;
      scene.add(m);
      pool.push({ mesh: m, t: 1, life: 1, from: new THREE.Vector3(), dir: new THREE.Vector3(), len: 1 });
    }
    return pool;
  })();
  let nextMeteor = 2.5;
  const MX = new THREE.Vector3(1, 0, 0);
  // Spawn them in CAMERA space. Fixed world points went off-screen the moment the
  // camera dollied back for a phone aspect, which is why the sky looked empty.
  const METEOR_DEPTH = 2.4;
  function launchMeteor() {
    const m = meteors.find(x => x.t >= 1);
    if (!m) return;
    const h = 2 * METEOR_DEPTH * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const w = h * camera.aspect;
    // enter high and off to one side, cross toward the opposite bottom corner
    const side = Math.random() < 0.5 ? -1 : 1;
    m.from.set(side * (0.18 + Math.random() * 0.42) * w,
               (0.10 + Math.random() * 0.42) * h,
               -METEOR_DEPTH);
    camera.localToWorld(m.from);
    m.dir.set(-side, -0.34 - Math.random() * 0.5, 0)
      .transformDirection(camera.matrixWorld).normalize();
    m.len  = (0.09 + Math.random() * 0.08) * h;
    m.life = 0.5 + Math.random() * 0.4;
    m.t = 0;
    m.mesh.scale.set(m.len, h * 0.3, 1);   // the plane is 0.026 tall, so this is the streak's waist
    m.mesh.visible = true;
  }

  /* ---------- post ---------- */
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(new OutputPass());

  /* ---------- state ---------- */
  let L, R, q0L, q0R, ready = false;
  const rig = new THREE.Group();
  scene.add(rig);
  let lensMat = null;

  // One number per temple: 1 folded, 0 open. There is no scene index - the beat you
  // are on IS how many temples are still folded, so a swipe can open either side in
  // either order and the copy still follows.
  const foldTo   = [1, 1];
  const foldFrom = [1, 1];
  const foldNow  = [1, 1];
  let tFold = 1;

  let offset = 0, activeName = '';   // continuous index into VARIANTS; fractional == mid-crossfade
  const topCols = VARIANTS.map(v => new THREE.Color(v.top));
  // tint2 straight off the customiser is a shadow colour, not a lens colour - taken
  // neat it reads as dirt at the bottom of the glass. Meet it 60% of the way.
  const botCols = VARIANTS.map(v => new THREE.Color(v.top).lerp(new THREE.Color(v.bot), 0.60));
  const mixTop = new THREE.Color(), mixBot = new THREE.Color();
  const lastTop = new THREE.Color(), lastBot = new THREE.Color();
  let lensT = null, lensColAttr = null;

  const AXIS = new THREE.Vector3(0, 1, 0);
  // Folded, both arms swing into the plane the lenses live in: measured, the temple
  // box straddles z -0.0078..0.0029 while the lens sheet sits at z -0.001, so they
  // literally cut through the glass and through each other. Real frames tuck them
  // BEHIND the front, one arm resting on the other. These are metres of world
  // clearance, applied only while folded and unwound as the temple swings out.
  const TUCK_NEAR = 0.006;    // first arm clears the lens sheet
  const TUCK_FAR  = 0.016;    // second arm rests behind the first
  const tuckDir = new THREE.Vector3();   // world -Z, expressed in the temples' parent space
  let tuckUnit = 1;
  const baseL = new THREE.Vector3(), baseR = new THREE.Vector3();
  const tmpQ = new THREE.Quaternion();
  const clock = new THREE.Clock();
  const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  /* ---------- sizing ----------
     Everything is measured off the SECTION, never the window: the hero is one block
     in a normal page, so innerHeight is the wrong number the moment anything sits
     above it. */
  let vw = 1, vh = 1;
  const wmCentre = new THREE.Vector3();
  let hdrClear = 0;   // px from the top of the hero that the header covers, plus a gap
  function resize() {
    const w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h || (w === vw && h === vh)) return;
    vw = w; vh = h;
    const bar = document.querySelector('.header__bar');
    hdrClear = (bar ? bar.offsetHeight : 0) + 16;   // the header floats over the top of the hero
    fitView(w, h);
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    placeCamera();
  }
  /* The cue sits just under the frame's own bottom edge, projected from the model
     rather than guessed as a % of the section: the camera reframes on resize, so a
     fixed percentage lands on the lenses at one width and in the copy at another. */
  const hintEl = root.querySelector('[data-h3d-spinhint]');
  const hintPt = new THREE.Vector3();
  let hintReady = false;
  function placeHint() {
    if (!hintReady || !hintEl) return;
    const p = hintPt.clone().project(camera);
    const y = (1 - p.y) / 2 * vh;
    hintEl.style.top = Math.round(y + vh * 0.035) + 'px';   // a small gap under the frame
    hintEl.style.left = Math.round((p.x + 1) / 2 * vw) + 'px';
  }

  new ResizeObserver(() => { resize(); placeHint(); }).observe(stage);

  /* ---------- model ---------- */
  new GLTFLoader().load(cfg.glb, gltf => {
    const model = gltf.scene;
    ['HingeSlot_L', 'HingeSlot_R'].forEach(n => model.getObjectByName(n)?.removeFromParent());

    const lens = model.getObjectByName('Lenses');
    const geo = lens.geometry, pos = geo.attributes.position;
    geo.computeBoundingBox();
    const { min, max } = geo.boundingBox, span = max.z - min.z;
    // how far down the lens each vertex sits (local +Z is world down). The gradient
    // is painted into vertex colours from that, so a tint can be a solid or a real
    // top-to-bottom fade without touching the geometry again.
    lensT = new Float32Array(pos.count);
    for (let i = 0; i < pos.count; i++) lensT[i] = (pos.getZ(i) - min.z) / span;
    lensColAttr = new THREE.BufferAttribute(new Float32Array(pos.count * 3), 3);
    geo.setAttribute('color', lensColAttr);
    lens.material = lens.material.clone();
    lens.material.vertexColors = true;
    lens.material.color.set(0xffffff);
    lens.material.transmission = 0.96;
    lens.material.thickness = 0.0006;
    lens.material.roughness = 0.02;
    lens.material.envMapIntensity = 0.35;
    // the lens sits flush inside the frame's inner lip and the two z-fight into a
    // dashed speckle along the bottom rim. Push the lens back a hair, in depth only.
    lens.material.polygonOffset = true;
    lens.material.polygonOffsetFactor = 1;
    lens.material.polygonOffsetUnits = 1;
    lensMat = lens.material;

    // Crystal and obsidian are the same geometry with different material names, so
    // match both. Crystal is opaque white + clearcoat + sheen, NOT transmission: a
    // second transmissive material doubles the pass the lens already pays for.
    model.traverse(o => {
      if (!o.isMesh || !o.material) return;
      const n = o.material.name;
      if (n === 'Acetate_Black' || n === 'Acetate_Crystal') {
        const m = new THREE.MeshPhysicalMaterial();
        THREE.Material.prototype.copy.call(m, o.material);
        m.color.copy(o.material.color);
        if (n === 'Acetate_Crystal') {
          m.color.set(0xe9edf2);          // warm-neutral white, not paper white
          m.sheen = 0.35;                 // the milky bloom acetate has at a grazing angle
          m.sheenColor = new THREE.Color(0xbfd2e8);
          m.sheenRoughness = 0.5;
        }
        m.metalness = 0;
        m.roughness = 0.10;
        m.clearcoat = 1;
        m.clearcoatRoughness = 0.045;
        m.envMapIntensity = 1.1;
        m.name = n;
        o.material = m;
      } else if (n.startsWith('Hinge_') || n.startsWith('Rivet_') || n.startsWith('Core_')) {
        o.material = o.material.clone();
        o.material.roughness = 0.16;
        o.material.envMapIntensity = 1.5;   // steel only reads as steel if it mirrors
        // The core is buried inside the acetate and the two skins are coincident, so
        // the steel breaks through as a dashed white hairline down the temple. Bias
        // the core back in depth only - nothing moves, it just always loses the tie.
        o.material.polygonOffset = true;
        o.material.polygonOffsetFactor = 2;
        o.material.polygonOffsetUnits = 2;
      }
    });

    L = model.getObjectByName('Temple_L');
    R = model.getObjectByName('Temple_R');
    q0L = L.quaternion.clone();
    q0R = R.quaternion.clone();
    baseL.copy(L.position); baseR.copy(R.position);
    // the temples hang off the front, whose axes are not the world's - derive "back"
    // rather than guessing which local axis points that way
    L.parent.updateWorldMatrix(true, false);
    const inv = new THREE.Matrix4().copy(L.parent.matrixWorld).invert();
    tuckDir.set(0, 0, -1).transformDirection(inv).normalize();
    tuckUnit = 1 / (L.parent.getWorldScale(new THREE.Vector3()).x || 1);

    // The steel cores are exported as scene roots, so they used to stay folded while
    // the temples swung out - two ghost arms across the lenses. attach() reparents
    // them without moving them, so the hinge carries them from here on.
    const coreL = model.getObjectByName('TempleCore_L');
    const coreR = model.getObjectByName('TempleCore_R');
    if (coreL) L.attach(coreL);
    if (coreR) R.attach(coreR);

    model.position.y = 0.024;
    rig.add(model);
    // folded, so the box is the frame front - the temples are tucked behind it
    const box = new THREE.Box3().setFromObject(model), c = box.getCenter(new THREE.Vector3());
    hintPt.set(c.x, box.min.y, c.z);
    hintReady = true;
    placeHint();
    ready = true;
    if (barEl) barEl.style.width = '100%';
    setTimeout(() => {           // one beat on a full bar, then hand over the screen
      loadEl?.classList.add('is-done');
      root.classList.add('is-ready');
      paint();
    }, 420);
  },
  e => { if (barEl && e.lengthComputable) barEl.style.width = (e.loaded / e.total * 92) + '%'; },
  () => { if (errEl) errEl.textContent = 'The 3D frame could not be loaded.'; });

  // 1 = the swatch straight off the customiser, 0 = clear glass.
  const TINT_DENSITY = 0.72;
  const WHITE = new THREE.Color(0xffffff);

  /* ---------- lens tint ----------
     One tint holds, then crossfades into the next, so the colour turns over rather
     than snapping. top/bot come off the product photos, so a tint where they differ
     stays a gradient and one where they match stays a solid. */
  function tintFromRing() {
    if (!lensMat) return;
    const f = ((offset % N) + N) % N;
    const i0 = Math.floor(f), i1 = (i0 + 1) % N;
    const w = f - i0;
    const e = w <= 0.55 ? 0 : ease(Math.min((w - 0.55) / 0.45, 1));   // hold, then turn
    mixTop.copy(topCols[i0]).lerp(topCols[i1], e);
    mixBot.copy(botCols[i0]).lerp(botCols[i1], e);
    // A transmissive material multiplies what passes through by this colour, so the
    // raw swatch reads as a dense pane. Wash it toward white and the tint stays the
    // same hue while the starfield comes through it.
    mixTop.lerp(WHITE, 1 - TINT_DENSITY);
    mixBot.lerp(WHITE, 1 - TINT_DENSITY);
    const best = e < 0.5 ? i0 : i1;

    // only repaint the ~9.6k vertices when the blend has actually moved
    const moved = Math.abs(mixTop.r - lastTop.r) + Math.abs(mixTop.g - lastTop.g) +
                  Math.abs(mixTop.b - lastTop.b) + Math.abs(mixBot.r - lastBot.r) +
                  Math.abs(mixBot.g - lastBot.g) + Math.abs(mixBot.b - lastBot.b);
    if (moved > 0.0015) {
      lastTop.copy(mixTop); lastBot.copy(mixBot);
      const a = lensColAttr.array;
      for (let i = 0; i < lensT.length; i++) {
        const t = lensT[i], d = 1.00 - t * 0.26;   // deeper toward the bottom edge
        a[i * 3]     = (mixTop.r + (mixBot.r - mixTop.r) * t) * d;
        a[i * 3 + 1] = (mixTop.g + (mixBot.g - mixTop.g) * t) * d;
        a[i * 3 + 2] = (mixTop.b + (mixBot.b - mixTop.b) * t) * d;
      }
      lensColAttr.needsUpdate = true;
    }

    if (VARIANTS[best].name !== activeName) {   // only touch the DOM when it changes
      activeName = VARIANTS[best].name;
      tintEl.textContent = activeName;
      tintEl.style.color = VARIANTS[best].top;
    }
  }

  /* ---------- the two beats ---------- */
  function openSide(side) {
    const i = SIDE[side];
    if (i === undefined || foldTo[i] === 0 || !ready) return;
    foldTo[i] = 0;
    foldFrom[0] = foldNow[0]; foldFrom[1] = foldNow[1];
    tFold = 0;
    paint();
  }
  const foldsLeft = () => foldTo[0] + foldTo[1];

  function paint() {
    const left = foldsLeft();
    const c = left === 2 ? COPY.both : left === 1 ? COPY.one : COPY.open;
    capT.textContent = c.t;
    capB.textContent = c.b;
    capB.hidden = !c.b;
    // the prompt only names directions that still do something: both folded, the
    // hand swipes right then left on a loop (client: right first); one open, it
    // cues the remaining side only
    const l = !!foldTo[SIDE.left], r = !!foldTo[SIDE.right];
    const dir = l && r ? 'both' : l ? 'left' : r ? 'right' : null;
    root.classList.toggle('is-open', !dir);
    root.classList.toggle('cue-both',  dir === 'both');
    root.classList.toggle('cue-left',  dir === 'left');
    root.classList.toggle('cue-right', dir === 'right');
  }

  /* ---------- input ----------
     Horizontal only. Vertical belongs to the page: no wheel handler, no
     touch-action:none, nothing that can swallow a scroll. */
  const SWIPE = 48;
  const cv = renderer.domElement;
  let downAt = null;
  // The frame keeps whatever angle you leave it at - no spring back to a locked shot,
  // and no coast either: it turns while the finger turns it and settles the instant
  // you stop. Angles accumulate from per-move deltas, not from the press point,
  // so a spin can go round as many times as the finger keeps pushing.
  // touch-action stays pan-y on the canvas, so a vertical drag is still the page's
  // scroll; the frame only takes over once the finger commits to travelling sideways.
  const SPIN_PER_W = Math.PI * 0.75;  // a full-width drag swings it ~135deg
  const PITCH_MAX  = 0.30;            // it is a shot of a frame, not a tumbling prop
  const SPIN_FOLLOW= 14;              // e-folds per second the frame chases the finger
  // yaw is unbounded: it is a 3D object, so it turns all the way round and keeps
  // going. Pitch stays clamped - past that it stops being a frame and starts being
  // a tumbling prop.
  // Two pairs on purpose: the drag writes the target, the frame eases toward it. No
  // velocity is kept, so letting go stops the turn - it never coasts on its own.
  let orbitYaw = 0, orbitPitch = 0, showYaw = 0, showPitch = 0, orbiting = false;
  // The spin cue is a demonstration, not decoration: the hand and the frame ride the
  // same wave, so the hint reads as the hand turning the frame. It fades out the
  // moment the viewer takes over, and the free spin below is untouched by it.
  const DEMO_PERIOD = 3.4, DEMO_YAW = 0.05, DEMO_SHIFT = 16;   // rad, % of the icon
  let demoT = 0, demoAmp = 0, demoYaw = 0;
  let lastPt = null;
  const onDown = e => {
    downAt = [e.clientX, e.clientY];
    lastPt = [e.clientX, e.clientY];
  };
  const onMove = e => {
    if (!downAt) return;
    if (foldsLeft()) return;      // the first two beats are the scripted swipes, not a spin
    if (!orbiting) {
      const tx = e.clientX - downAt[0], ty = e.clientY - downAt[1];
      if (Math.abs(tx) < 6 || Math.abs(tx) <= Math.abs(ty)) return;   // still could be a scroll
      orbiting = true;
      root.classList.add('is-spinning', 'has-spun');   // has-spun retires the hint for good
      lastPt = [e.clientX, e.clientY];
    }
    const dx = e.clientX - lastPt[0], dy = e.clientY - lastPt[1];
    lastPt = [e.clientX, e.clientY];

    const ry = (dx / vw) * SPIN_PER_W;
    // drag down, the frame's top rolls away from you: rotation.x follows -dy, or it
    // reads as the frame moving the opposite way to the finger
    const rp = -(dy / vh) * SPIN_PER_W * 0.5;
    orbitYaw += ry;
    orbitPitch = THREE.MathUtils.clamp(orbitPitch + rp, -PITCH_MAX, PITCH_MAX);
  };
  const endOrbit = () => { orbiting = false; lastPt = null; root.classList.remove('is-spinning'); };
  const onUp = e => {
    if (!downAt) return;
    const dx = e.clientX - downAt[0], dy = e.clientY - downAt[1];
    downAt = null;
    if (Math.abs(dx) < SWIPE || Math.abs(dx) <= Math.abs(dy)) return;   // that was a scroll
    openSide(dx > 0 ? 'right' : 'left');
  };
  cv.addEventListener('pointerdown', e => { try { cv.setPointerCapture(e.pointerId); } catch (_) {} onDown(e); });
  cv.addEventListener('pointermove', onMove);
  cv.addEventListener('pointerup', e => { endOrbit(); onUp(e); });
  cv.addEventListener('pointercancel', () => { downAt = null; endOrbit(); });
  root.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') openSide('right');
    else if (e.key === 'ArrowLeft') openSide('left');
  });

  /* ---------- loop ---------- */
  // headless Chrome throttles requestAnimationFrame to ~2fps and no launch flag
  // fixes it, so _hero3d-check.py cannot wait out a 1.5s ease in wall-clock time.
  // This lets it run the same animation on a faster sim clock instead of loosening
  // every assertion. Untouched in a real browser.
  let timeScale = 1;
  window.__h3dScale = k => { timeScale = k; };

  let clk = 0, visible = true;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; },
    { rootMargin: '80px' }).observe(root);

  resize();
  placeHint();
  paint();

  renderer.setAnimationLoop(() => {
    // off-screen, or under the phone menu (re-blurring a live canvas every frame lags): draw nothing
    if (!visible || document.body.classList.contains('nav-open')) { clock.getDelta(); return; }
    const dt = Math.min(clock.getDelta(), 0.05) * timeScale;
    clk += dt;

    /* temples */
    if (tFold < 1) {
      tFold = Math.min(tFold + dt / FOLD_DUR, 1);
      const e = ease(tFold);
      for (let i = 0; i < 2; i++) foldNow[i] = foldFrom[i] + (foldTo[i] - foldFrom[i]) * e;
    }

    /* lens tint: it walks the ring only once BOTH temples are out. While the frame
       is still folding the lens holds what it opened with, so the two things never
       compete for the eye. */
    if (ready && foldsLeft() === 0 && tFold >= 1) {
      offset += dt / TINT_HOLD;
      tintFromRing();
    } else if (ready && !activeName) tintFromRing();   // paint it once, then leave it

    /* shooting stars */
    nextMeteor -= dt;
    if (nextMeteor <= 0) { launchMeteor(); nextMeteor = 1.4 + Math.random() * 2.4; }
    for (const m of meteors) {
      if (m.t >= 1) { m.mesh.visible = false; continue; }
      m.t += dt / m.life;
      const d = Math.min(m.t, 1);
      m.mesh.position.copy(m.from).addScaledVector(m.dir, d * METEOR_DEPTH * 1.3);
      m.mesh.quaternion.setFromUnitVectors(MX, m.dir);
      m.mesh.material.opacity = Math.min(d * 7, 1) * (1 - d) * (1 - d) * 1.6;
    }

    brand3d.material.opacity += (BRAND_OPACITY - brand3d.material.opacity) * Math.min(dt * 3, 1);
    // the wordmark is a fixed 0.6m plane, so a narrow viewport crops it mid-word.
    // scale it to whatever the camera can actually see at its own depth.
    if (brand3d.material.opacity > 0.005) {
      const d = camera.position.distanceTo(brand3d.position);
      const visH = 2 * d * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
      const visW = visH * camera.aspect;
      let s = Math.min(1, visW * cfg.wordmark_fit * wmBoost / 0.60);
      // Short, wide screens (laptops): width-based sizing pushed the "AECO ORIGINALS"
      // eyebrow up under the navbar. Cap it so the eyebrow's top stays below the header.
      // The eyebrow sits 0.383 of the plane height above its centre (texture row ~75 of 640).
      wmCentre.copy(brand3d.position).project(camera);
      const room = (1 - wmCentre.y) / 2 * vh - hdrClear;     // px between header and wordmark centre
      if (room > 0) s = Math.min(s, room / (vh / visH * 0.1875 * 0.383));
      brand3d.scale.setScalar(s);
    }

    /* weightless drift + drag orbit.
       Both write the same three angles, so they are composed here rather than one
       of them doing a += on whatever the other left behind. The drift is a small
       float around wherever the spin left the frame - it never pulls it home. */
    const follow = 1 - Math.exp(-SPIN_FOLLOW * dt);   // frame-rate independent ease
    showYaw   += (orbitYaw   - showYaw)   * follow;
    showPitch += (orbitPitch - showPitch) * follow;
    const demoOn = !REDUCED && !foldsLeft() && !root.classList.contains('has-spun');
    demoAmp += ((demoOn ? 1 : 0) - demoAmp) * Math.min(dt * 2.5, 1);   // ease in, ease out
    if (demoAmp > 0.002) {
      demoT += dt;
      const w = Math.sin(demoT * 2 * Math.PI / DEMO_PERIOD) * demoAmp;
      demoYaw = w * DEMO_YAW;
      if (hintSvg) hintSvg.style.transform = `translateX(${(w * DEMO_SHIFT).toFixed(2)}%)`;
    } else {
      demoYaw = 0;
    }

    if (!REDUCED) {
      rig.position.y = Math.sin(clk * 0.55) * 0.0018;
      rig.rotation.z = Math.sin(clk * 0.38) * 0.008;
      rig.rotation.x = Math.sin(clk * 0.29) * 0.005 + showPitch;
      stars.rotation.y += dt * 0.006;
    } else {
      rig.rotation.x = showPitch;
    }
    rig.rotation.y = showYaw + demoYaw;

    fill.position.copy(camera.position).multiplyScalar(1.6);

    if (ready) {
      L.quaternion.copy(q0L).multiply(tmpQ.setFromAxisAngle(AXIS, FOLD_L * foldNow[0]));
      R.quaternion.copy(q0R).multiply(tmpQ.setFromAxisAngle(AXIS, FOLD_R * foldNow[1]));
      // tuck: full while folded, gone by the time the arm is out
      L.position.copy(baseL).addScaledVector(tuckDir, TUCK_FAR  * tuckUnit * foldNow[0]);
      R.position.copy(baseR).addScaledVector(tuckDir, TUCK_NEAR * tuckUnit * foldNow[1]);
    }
    composer.render();
  });

  // test + Shopify-editor hooks
  root.__h3d = {
    dbg: () => ({ ready, side: SIDE, foldTo: [...foldTo], foldNow: foldNow.map(v => +v.toFixed(2)),
                  tFold: +tFold.toFixed(3), tint: activeName, offset: +offset.toFixed(3),
                  size: [vw, vh], cam: camera.position.toArray().map(v => +v.toFixed(3)) }),
    open: side => openSide(side),
    seek: v => { offset = v; tintFromRing(); },
    reset: () => { foldTo[0] = foldTo[1] = 1; foldNow[0] = foldNow[1] = 1;
                   foldFrom[0] = foldFrom[1] = 1; tFold = 1; offset = 0; paint(); },
  };
}

document.querySelectorAll('[data-h3d]').forEach(boot);
// the theme editor re-renders a section into a fresh node, so re-run on its event
document.addEventListener('shopify:section:load', e => {
  e.target.querySelectorAll?.('[data-h3d]').forEach(boot);
});
